import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { pointsApi } from '@/api/points';
import { friendlyMessage } from '@/lib/errors';

/**
 * Resolve o QR físico de ESTAÇÃO escaneado dentro do app (`/scan/:hubStationId`
 * — o mesmo QR que abre a landing pública quando lido pela câmera do sistema).
 *
 *   1 conector  → /qr/{hub}/{connectorNumber} (QRHandler: auto-start)
 *   N conectores → /configure/{pointId}/{primeiro} (usuário escolhe o cabo)
 *
 * Espelha a decisão da landing pública em 2 passos, pulando o passo de
 * "escolher app" — quem escaneou daqui já está no app Bow.
 */
export default function ScanResolve() {
  const { hubStationId } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!hubStationId) {
      setError('QR inválido');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const detail = await pointsApi.getByHubId(hubStationId);
        if (cancelled) return;
        if (detail.connectors.length === 0) {
          setError('Este carregador está sem conectores cadastrados.');
          return;
        }
        if (detail.connectors.length === 1) {
          navigate(
            `/qr/${encodeURIComponent(hubStationId)}/${detail.connectors[0].connector_number}`,
            { replace: true }
          );
        } else {
          // viaQr: quem escaneou está no carregador — sem a trava de
          // "eletroposto errado" do ConfigureCharge.
          navigate(`/configure/${detail.id}/${detail.connectors[0].id}`, {
            replace: true,
            state: { viaQr: true }
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(friendlyMessage(err, 'Carregador não encontrado'));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hubStationId, navigate]);

  if (error) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-2xl font-black">QR não reconhecido</h1>
        <div className="rounded-2xl bg-white p-4 text-sm text-bow-red">{error}</div>
        <Link
          to="/"
          className="inline-flex h-11 items-center justify-center rounded-xl bg-bow-blue px-5 font-black text-white"
        >
          Voltar
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-3 p-8">
      <div className="h-12 w-12 animate-spin rounded-full border-2 border-bow-royal/20 border-t-bow-blue" />
      <p className="text-sm text-bow-royal/70">Identificando carregador…</p>
    </div>
  );
}
